import { Play, ChevronsLeft, ChevronsRight } from "lucide-react";
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { Tables } from "@/integrations/supabase/types";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import type { TrainingVideoTabBucket } from "../trainingVideoCurriculumTabs";
import {
  compareTrainingVideoLabels,
  getTrainingVideoListLabel,
  uncategorizedTrainingVideoTabBucket,
} from "../trainingVideoCurriculumTabs";

type ObjectMediaRow = Tables<"object_media">;

interface MembersVideosTabProps {
  isLoading: boolean;
  error: string | null;
  videos: ObjectMediaRow[];
  videoTabBucketByMediaId: Record<string, TrainingVideoTabBucket>;
  onSelectVideo: (video: ObjectMediaRow) => void;
}

export default function MembersVideosTab({
  isLoading,
  error,
  videos,
  videoTabBucketByMediaId,
  onSelectVideo,
}: MembersVideosTabProps) {
  const trainingNavScrollRef = useRef<HTMLDivElement | null>(null);
  const [showTrainingNavLeftHint, setShowTrainingNavLeftHint] = useState(false);
  const [showTrainingNavRightHint, setShowTrainingNavRightHint] = useState(false);
  const [activeTrainingSubject, setActiveTrainingSubject] = useState("");

  const bucketForVideo = useCallback(
    (video: ObjectMediaRow): TrainingVideoTabBucket =>
      videoTabBucketByMediaId[video.id] ?? uncategorizedTrainingVideoTabBucket(),
    [videoTabBucketByMediaId],
  );

  const groups = useMemo(() => {
    const m = new Map<string, ObjectMediaRow[]>();
    for (const v of videos) {
      const b = bucketForVideo(v);
      const arr = m.get(b.tabKey);
      if (arr) arr.push(v);
      else m.set(b.tabKey, [v]);
    }
    for (const [, list] of m) {
      list.sort((a, b) => {
        const cmp = compareTrainingVideoLabels(a, b);
        if (cmp !== 0) return cmp;
        return a.id.localeCompare(b.id);
      });
    }
    return m;
  }, [videos, bucketForVideo]);

  const orderedTabs = useMemo(() => {
    const seen = new Map<string, TrainingVideoTabBucket>();
    for (const v of videos) {
      const b = bucketForVideo(v);
      if (!seen.has(b.tabKey)) seen.set(b.tabKey, b);
    }
    return [...seen.values()].sort((a, b) => {
      if (a.sortIndex !== b.sortIndex) return a.sortIndex - b.sortIndex;
      const byLabel = a.tabLabel.localeCompare(b.tabLabel);
      if (byLabel !== 0) return byLabel;
      return a.tabKey.localeCompare(b.tabKey);
    });
  }, [videos, bucketForVideo]);

  const tabFingerprint = orderedTabs.map((t) => t.tabKey).join("|");

  useLayoutEffect(() => {
    if (orderedTabs.length === 0) {
      setActiveTrainingSubject("");
      return;
    }
    setActiveTrainingSubject((prev) =>
      orderedTabs.some((t) => t.tabKey === prev) ? prev : orderedTabs[0].tabKey,
    );
  }, [tabFingerprint, orderedTabs]);

  const updateTrainingNavOverflowHints = useCallback(() => {
    const container = trainingNavScrollRef.current;
    if (!container) return;

    const { scrollLeft, clientWidth, scrollWidth } = container;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    if (scrollLeft > maxScrollLeft) {
      container.scrollLeft = maxScrollLeft;
    }

    setShowTrainingNavLeftHint(container.scrollLeft > 4);
    setShowTrainingNavRightHint(maxScrollLeft - container.scrollLeft > 4);
  }, []);

  useLayoutEffect(() => {
    updateTrainingNavOverflowHints();
    requestAnimationFrame(updateTrainingNavOverflowHints);
  }, [tabFingerprint, activeTrainingSubject, updateTrainingNavOverflowHints]);

  const scrollHorizontalTabStrip = useCallback(
    (stripRef: RefObject<HTMLDivElement | null>, direction: "backward" | "forward") => {
      const el = stripRef.current;
      if (!el) return;
      const delta = Math.max(160, Math.round(el.clientWidth * 0.65));
      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const nextLeft =
        direction === "backward"
          ? Math.max(0, el.scrollLeft - delta)
          : Math.min(maxScrollLeft, el.scrollLeft + delta);
      el.scrollTo({ left: nextLeft, behavior: "smooth" });
    },
    [],
  );

  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-transparent px-4 py-2 text-sm font-medium transition-shadow",
    "!text-gray-600 data-[state=active]:z-10 data-[state=active]:!border-black/10 data-[state=active]:!bg-white data-[state=active]:!text-gray-900 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/15 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  const renderVideoButton = (video: ObjectMediaRow) => (
    <button
      key={video.id}
      type="button"
      onClick={() => onSelectVideo(video)}
      className="w-full flex items-center justify-between px-3 py-2 rounded hover:bg-gray-100 text-left"
    >
      <span className="text-sm">{getTrainingVideoListLabel(video)}</span>
      <Play className="h-4 w-4 shrink-0 text-taekwondo-blue" />
    </button>
  );

  return (
    <div className="p-4 md:p-6">
      <h2 className="text-2xl font-semibold mb-6 text-taekwondo-red">Training Videos</h2>
      {isLoading ? (
        <p className="text-gray-700">Loading training videos...</p>
      ) : error ? (
        <p className="text-red-600 text-sm">{error}</p>
      ) : videos.length === 0 ? (
        <div className="bg-gray-50 rounded p-4">
          <p className="text-gray-700">
            Training videos will be available soon. Check back later for instructional videos.
          </p>
        </div>
      ) : (
        <div className="mb-8">
          <p className="block text-lg font-medium mb-1">Select Subject</p>
          <Tabs
            value={activeTrainingSubject}
            onValueChange={setActiveTrainingSubject}
            className="w-full space-y-4"
          >
            <div className="relative border-b bg-white/95 backdrop-blur md:border-0 md:bg-transparent">
              <div
                ref={trainingNavScrollRef}
                onScroll={updateTrainingNavOverflowHints}
                className="touch-pan-x overflow-x-auto px-2 py-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              >
                <TabsList className="inline-flex h-auto w-max min-w-0 flex-nowrap justify-start gap-2 rounded-full bg-gray-100 p-1">
                  {orderedTabs.map((t) => (
                    <TabsTrigger key={t.tabKey} value={t.tabKey} className={triggerClass}>
                      <span className="md:hidden">{t.tabMobileLabel}</span>
                      <span className="hidden md:inline">{t.tabLabel}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              {showTrainingNavLeftHint && (
                <div className="pointer-events-none absolute inset-y-0 left-0 z-30 flex w-14 items-center justify-start bg-gradient-to-r from-white via-white/90 to-transparent pl-2">
                  <button
                    type="button"
                    className="pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow-sm ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-gray-400"
                    aria-label="Scroll training subject tabs left"
                    onClick={() => scrollHorizontalTabStrip(trainingNavScrollRef, "backward")}
                  >
                    <ChevronsLeft className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              )}
              {showTrainingNavRightHint && (
                <div className="pointer-events-none absolute inset-y-0 right-0 z-30 flex w-14 items-center justify-end bg-gradient-to-l from-white via-white/90 to-transparent pr-2">
                  <button
                    type="button"
                    className="pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow-sm ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-gray-400"
                    aria-label="Scroll training subject tabs right"
                    onClick={() => scrollHorizontalTabStrip(trainingNavScrollRef, "forward")}
                  >
                    <ChevronsRight className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              )}
            </div>

            {orderedTabs.map((t) => (
              <TabsContent key={t.tabKey} value={t.tabKey} className="mt-0 focus-visible:outline-none">
                <div className="rounded-lg bg-gray-50 p-4 space-y-1">
                  {(groups.get(t.tabKey) ?? []).map((video) => renderVideoButton(video))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      )}
    </div>
  );
}
